import React, { useMemo } from "react";
import { code128Layout, isCode128BEncodable } from "../../functions/code128";

/**
 * A Code 128 barcode as inline SVG.
 *
 * Rendered synchronously from pure data (functions/code128), not drawn into a node in an effect:
 * the orders page prints with react-to-print on a fixed timer, and a barcode that is still being
 * drawn when the print starts comes out blank. Sized in millimetres, because what matters is the
 * printed module width a scanner has to resolve, not pixels on a screen.
 *
 * @param {string} value - what the barcode encodes (printable ASCII)
 * @param {number} moduleMm - width of the narrowest bar; 0.5 mm reads comfortably from arm's length
 * @param {number} heightMm - bar height
 * @param {boolean} showValue - print the value in text under the bars
 */
const Barcode = ({ value, moduleMm = 0.33, heightMm = 15, quietZone = 10, showValue = false, fontSizeMm = 3, style }) => {
  const layout = useMemo(
    () => (isCode128BEncodable(value) ? code128Layout(value, { quietZone }) : null),
    [value, quietZone],
  );

  if (!layout) {
    return (
      <span style={{ color: "#cf1322", fontWeight: 600, ...style }}>
        Ongeldige barcode: {String(value === undefined || value === null ? "(leeg)" : value)}
      </span>
    );
  }

  // Everything inside the SVG is in modules, so bar positions stay whole numbers and crisp
  const barHeight = heightMm / moduleMm;
  const textHeight = showValue ? (fontSizeMm * 1.5) / moduleMm : 0;
  const totalHeight = barHeight + textHeight;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={`${layout.totalModules * moduleMm}mm`}
      height={`${totalHeight * moduleMm}mm`}
      viewBox={`0 0 ${layout.totalModules} ${totalHeight}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={value}
      data-barcode={value}
      style={{ display: "block", ...style }}
    >
      <rect x="0" y="0" width={layout.totalModules} height={totalHeight} fill="#fff" />
      {layout.bars.map((bar) => (
        <rect key={bar.x} x={bar.x} y="0" width={bar.width} height={barHeight} fill="#000" />
      ))}
      {showValue && (
        <text
          x={layout.totalModules / 2}
          y={barHeight + (fontSizeMm * 1.15) / moduleMm}
          textAnchor="middle"
          fontFamily="Arial, Helvetica, sans-serif"
          fontSize={fontSizeMm / moduleMm}
          fill="#000"
        >
          {value}
        </text>
      )}
    </svg>
  );
};

export default Barcode;
